import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import LookupSelect from '../components/LookupSelect';
import { lookupUser } from '../lookups';

/**
 * Add a participant to a purchased product.
 *
 * The old form takes two GUIDs and validates both against a regex. The user one
 * is a `LookupSelect` here — you rarely know a user by their id, and the search
 * is the same one every other page uses.
 *
 * The purchase product stays a typed id: no endpoint looks one up by name, and a
 * purchase product has no name to look up. Where you get it from is the answer
 * this field needs, so the hint says it: a purchase's product tab.
 *
 * On success the old form goes to the new participant's page, and so does this.
 */

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ParticipantCreateInternal = ({ api }) => {
  const navigate = useNavigate();

  const [userId, setUserId] = useState('');
  const [purchaseProductId, setPurchaseProductId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const productLooksRight = GUID.test(purchaseProductId.trim());
  const canSubmit = !!userId && productLooksRight && !busy;

  const submit = async event => {
    event.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setError(null);
    try {
      const created = await api.participants.addParticipant({
        UserId: userId,
        PurchaseProductId: purchaseProductId.trim()
      });

      const id = created?.Id || created?.id;
      if (!id) {
        // The call came back without an id, so there is nothing to navigate to
        // and claiming success would be a guess.
        setError('The participant may not have been created — the server returned no id.');
        return;
      }
      navigate(`/next/participant/${id}`);
    } catch (exception) {
      setError(exception?.response?.data?.ErrorMessage
        || exception?.message
        || 'Unable to add the participant.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="next-page">
      <BackToList to="/next/participants">← All participants</BackToList>

      <header className="next-page__header">
        <h1>Add a participant</h1>
        <p className="next-muted">Puts an existing user onto an already purchased product.</p>
      </header>

      {error && (
        <div className="next-note next-note--error">
          {error}
          <button type="button" className="next-note__close" onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <section className="next-card">
        <form className="next-form" onSubmit={submit}>
          <div className="next-form__row">
            <LookupSelect
              label="User"
              value={userId}
              onChange={setUserId}
              loadOptions={key => lookupUser(api, key)}
              placeholder="Name, email or id"
            />
          </div>

          <div className="next-form__row">
            <label className="next-filter">
              <span className="next-filter__label">Purchase product id</span>
              <input
                className="next-input"
                value={purchaseProductId}
                placeholder="00000000-0000-0000-0000-000000000000"
                onChange={event => setPurchaseProductId(event.target.value)}
              />
              <span className="next-form__hint">
                {purchaseProductId && !productLooksRight
                  ? 'That is not a UUID.'
                  : 'From the purchase’s product — its Participants tab lists the ones already on it.'}
              </span>
            </label>
          </div>

          <div className="next-form__actions">
            <button type="submit" className="next-btn next-btn--primary" disabled={!canSubmit}>
              {busy ? 'Adding…' : 'Add participant'}
            </button>
            <Link className="next-btn" to="/next/participants">Cancel</Link>
          </div>
        </form>
      </section>
    </div>
  );
};

ParticipantCreateInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const ParticipantCreate = withApi(ParticipantCreateInternal);

ParticipantCreate.requiresLogin = true;

export default ParticipantCreate;
